import { http } from "@/utils/http";

export interface SendMessageRequest {
  receiverId: number;
  subject: string;
  content: string;
}

export interface MessageResponse {
  id: number;
  senderId: number;
  senderName: string;
  senderEmail: string;
  receiverId: number;
  receiverName: string;
  receiverEmail: string;
  subject: string;
  content: string;
  isRead: boolean;
  sentAt: string;
  readAt: string | null;
}

export interface PagedMessagesResponse {
  content: MessageResponse[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const messagesApi = {
  /**
   * Send a message
   */
  async sendMessage(request: SendMessageRequest): Promise<MessageResponse> {
    const response = await http.post("/api/messages", request);
    return response.data;
  },

  /**
   * Send feedback to admin (receiverId is ignored, auto-sent to admin)
   */
  async sendFeedback(request: SendMessageRequest): Promise<MessageResponse> {
    const response = await http.post("/api/messages/feedback", request);
    return response.data;
  },

  /**
   * Get inbox messages (received)
   */
  async getInbox(page: number = 0, size: number = 20): Promise<PagedMessagesResponse> {
    const response = await http.get("/api/messages/inbox", {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Get sent messages (outbox)
   */
  async getSent(page: number = 0, size: number = 20): Promise<PagedMessagesResponse> {
    const response = await http.get("/api/messages/sent", {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Get a specific message
   */
  async getMessage(id: number): Promise<MessageResponse> {
    const response = await http.get(`/api/messages/${id}`);
    return response.data;
  },

  /**
   * Mark message as read
   */
  async markAsRead(id: number): Promise<MessageResponse> {
    const response = await http.put(`/api/messages/${id}/read`);
    return response.data;
  },

  /**
   * Delete a message
   */
  async deleteMessage(id: number): Promise<void> {
    await http.delete(`/api/messages/${id}`);
  },

  /**
   * Get unread message count
   */
  async getUnreadCount(): Promise<{ count: number }> {
    const response = await http.get("/api/messages/unread/count");
    return response.data;
  },
};
